<template>
    <v-dialog 
        v-model="dialog" 
        max-width="800"
        persistent
        :retain-focus="false"
        transition="dialog-bottom-transition"
    >
        <v-card class="damaged-dialog-card" :loading="loading">
            <!-- Close Button - Fixed Position Top Right -->
            <v-btn 
                icon 
                large
                color="white"
                @click="closeModal"
                class="close-button-fixed"
            >
                <v-icon size="28" color="grey darken-2">mdi-close</v-icon>
            </v-btn>
            
            <!-- Enhanced Header -->
            <v-card-title 
                class="enhanced-damaged-header white--text d-flex align-center pa-6"
                :class="{ 'reset-mode': resetOnly }"
            >
                <div class="d-flex align-center">
                    <v-avatar color="white" size="48" class="mr-4">
                        <v-icon color="orange" size="28">
                            {{ resetOnly ? 'mdi-refresh-circle' : 'mdi-alert-octagon' }}
                        </v-icon>
                    </v-avatar>
                    <div>
                        <h2 class="text-h5 font-weight-bold mb-1">
                            {{ resetOnly ? 'Artikel zurücksetzen' : 'Fehlgravur melden' }}
                        </h2>
                        <p class="text-body-1 mb-0 opacity-90">
                            {{ 
                                'Der Artikel wird in die Produktion zurückgeschleust.'
                            }}
                        </p>
                    </div>
                </div>
            </v-card-title>

            <!-- Enhanced Content -->
            <v-card-text class="enhanced-content pa-6">

                <!-- Error Display -->
                <v-alert
                    v-if="error"
                    type="error"
                    dismissible
                    @input="error = null"
                    class="mb-4"
                >
                    <strong>Fehler beim Verarbeiten:</strong> {{ error }}
                </v-alert>

                <!-- Articles Grid Display -->
                <div class="articles-grid-container">
                    <!-- Info Banner -->
                    <v-alert
                        type="info"
                        outlined
                        dense
                        class="mb-4"
                    >
                        <div class="d-flex align-items-center">
                            <v-icon left>mdi-information</v-icon>
                            <span>Klicken Sie auf die Artikel, die zurückgesetzt werden sollen</span>
                        </div>
                    </v-alert>
                    
                    <!-- Articles Grid (3 columns) -->
                    <div class="articles-grid">
                        <v-card
                            v-for="product in orderProducts"
                            :key="getProductUniqueId(product)"
                            class="article-reset-card"
                            :class="{ 'selected': isArticleSelected(product) }"
                            @click="toggleArticleSelection(product)"
                            elevation="2"
                        >
                            <!-- Selection Checkmark -->
                            <div v-if="isArticleSelected(product)" class="selection-checkmark">
                                <v-icon color="white" size="20">mdi-check-circle</v-icon>
                            </div>
                            
                            <!-- Article Image -->
                            <div class="article-preview-image">
                                <v-img
                                    v-if="getImageUrl(product)"
                                    :src="getImageUrl(product)"
                                    height="120"
                                    contain
                                >
                                    <template v-slot:placeholder>
                                        <div class="image-loading">
                                            <v-icon color="grey">mdi-image</v-icon>
                                        </div>
                                    </template>
                                </v-img>
                                <div v-else class="no-image-placeholder">
                                    <v-icon size="48" color="grey lighten-1">mdi-image-off</v-icon>
                                </div>
                            </div>
                            
                            <!-- Article Info -->
                            <v-card-text class="pa-3">
                                <div class="article-sku-badge mb-2">{{ product.sku }}</div>
                                
                                <!-- Quantity -->
                                <div v-if="product.qty > 1" class="article-qty mb-2">
                                    <v-chip x-small color="red" dark>
                                        {{ product.qty }}x
                                    </v-chip>
                                </div>
                                
                                <!-- Scanned Status -->
                                <div v-if="product.scannedCount" class="article-scan-status mb-2">
                                    <v-chip x-small color="blue" dark>
                                        <v-icon left x-small>mdi-check</v-icon>
                                        {{ product.scannedCount }}/{{ product.qty || 1 }}
                                    </v-chip>
                                </div>
                                
                                <!-- Name/Wunschname -->
                                <div class="article-name">
                                    {{ getArticleName(product) }}
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>
                    
                    <!-- Selection Summary -->
                    <div v-if="selectedArticles.length > 0" class="mt-4">
                        <v-alert
                            type="success"
                            dense
                            outlined
                        >
                            <div class="d-flex align-items-center justify-space-between">
                                <div class="d-flex align-items-center">
                                    <v-icon left small>mdi-check-circle</v-icon>
                                    <span class="text-caption">
                                        <strong>{{ selectedArticles.length }}</strong> Artikel ausgewählt
                                    </span>
                                </div>
                                <v-btn
                                    x-small
                                    text
                                    color="orange"
                                    @click="clearSelection"
                                >
                                    <v-icon left x-small>mdi-close</v-icon>
                                    Auswahl löschen
                                </v-btn>
                            </div>
                        </v-alert>
                    </div>
                </div>

                

            </v-card-text>

            <!-- Enhanced Footer -->
            <v-divider></v-divider>
            
            <v-card-actions class="enhanced-footer pa-6 d-flex justify-end">
                <v-btn 
                    text 
                    large
                    @click="closeModal"
                    class="mr-3"
                >
                    <v-icon left>mdi-close</v-icon>
                    Abbrechen
                </v-btn>
                <v-btn 
                    color="orange"
                    large
                    :loading="loading"
                    @click="applyReset"
                    class="px-6"
                    dark
                >
                    <v-icon left>
                        {{ resetOnly ? 'mdi-refresh-circle' : 'mdi-alert-octagon' }}
                    </v-icon>
                    {{ resetOnly ? 'Artikel zurücksetzen' : 'Fehlgravur eintragen' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import PacklistService from '@/services/PacklistService';

export default {
    name: 'DamagedDialog',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        article: {
            type: Object,
            default: null
        },
        orderId: {
            type: String,
            default: null
        },
        resetOnly: {
            type: Boolean,
            default: true
        },
        orderProducts: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            loading: false,
            error: null,
            dialog: false,
            articleData: null,
            additionalArticles: [],
            selectedArticles: []
        }
    },
    computed: {
        availableAdditionalArticles() {
            if (!this.orderProducts || !this.articleData) {
                return [];
            }
            
            // Filter out the main article being reset
            return this.orderProducts
                .filter(product => product.sku !== this.articleData.sku)
                .map(product => ({
                    sku: product.sku,
                    label: `${product.sku}${product.qty > 1 ? ` (${product.qty}x)` : ''}`,
                    qty: product.qty || 1,
                    scannedCount: product.scannedCount,
                    designId: product.designId,
                    translated: product.translated,
                    attributes: product.attributes
                }));
        },
        
        allAvailableArticles() {
            // Used when no specific article is provided (bulk reset mode)
            if (!this.orderProducts) {
                return [];
            }
            
            return this.orderProducts.map(product => ({
                sku: product.sku,
                label: `${product.sku}${product.qty > 1 ? ` (${product.qty}x)` : ''}`,
                qty: product.qty || 1,
                scannedCount: product.scannedCount,
                designId: product.designId,
                translated: product.translated,
                attributes: product.attributes
            }));
        }
    },
    watch: {
        show(newVal) {
            this.dialog = newVal;
            if (newVal && this.article) {
                this.articleData = { 
                    ...this.article
                };
                
                // Debug logging
                console.log('🔍 [DAMAGED DIALOG] Dialog opened with:', {
                    orderId: this.orderId,
                    article: this.article,
                    resetOnly: this.resetOnly
                });
            }
        },
        dialog(newVal) {
            if (!newVal) {
                this.$emit('close');
            }
        }
    },
    methods: {
        closeModal() {
            this.dialog = false;
            this.error = null;
            this.loading = false;
            this.articleData = null;
            this.additionalArticles = [];
            this.selectedArticles = [];
        },
        
        removeAdditionalArticle(sku) {
            this.additionalArticles = this.additionalArticles.filter(s => s !== sku);
        },
        
        removeSelectedArticle(productId) {
            this.selectedArticles = this.selectedArticles.filter(s => s !== productId);
        },

        getProductUniqueId(product) {
            // Create unique ID using designId if available, otherwise fall back to SKU
            const designId = product.designId || product.attributes?.designId;
            return designId ? `${product.sku}-${designId}` : product.sku;
        },

        toggleArticleSelection(product) {
            const productId = this.getProductUniqueId(product);
            const index = this.selectedArticles.indexOf(productId);
            if (index > -1) {
                // Already selected - remove it
                this.selectedArticles.splice(index, 1);
            } else {
                // Not selected - add it
                this.selectedArticles.push(productId);
            }
        },

        isArticleSelected(product) {
            const productId = this.getProductUniqueId(product);
            return this.selectedArticles.includes(productId);
        },
        
        getArticleName(product) {
            // Try to get personalization name from various sources
            if (product.attributes?.Wunschname) {
                return product.attributes.Wunschname;
            }
            if (product.attributes?.name) {
                return product.attributes.name;
            }
            if (product.attributes?.Name) {
                return product.attributes.Name;
            }
            if (product.translated?.line1 && !this.isImageUrl(product.translated.line1)) {
                return product.translated.line1;
            }
            if (product.translated?.sku) {
                return product.translated.sku;
            }
            return 'Keine Personalisierung';
        },
        
        clearSelection() {
            this.selectedArticles = [];
        },
        
        
        getImageUrl(item) {
            // Get image from various possible sources
            if (item.translated?.line1 && this.isImageUrl(item.translated.line1)) {
                return item.translated.line1;
            }
            
            let hasDesignId = item.designId || (item.attributes && item.attributes.designId);
            let hasPreview = item.attributes && item.attributes.preview;

            if (hasDesignId) {
                return `https://accelery-poster.s3.eu-central-1.amazonaws.com/${encodeURIComponent(hasDesignId)}.png`;
            } else if (hasPreview) {
                return hasPreview[0]?.url || hasPreview;
            } else if (item.attributes && typeof item.attributes === 'object') {
                // Look for any URL in attributes
                for (const [key, value] of Object.entries(item.attributes)) {
                    if (typeof value === 'string' && this.isImageUrl(value)) {
                        return value;
                    }
                }
            }
            return null;
        },
        
        getFilteredAttributes(item) {
            if (!item.attributes || typeof item.attributes !== 'object') return [];
            
            return Object.entries(item.attributes)
                .filter(([key, value]) => {
                    // Show line attributes and personalization
                    const isLineAttribute = key.toLowerCase().includes('line') || key.toLowerCase().includes('name') || key.toLowerCase().includes('gravur');
                    const isNotImageUrl = !this.isImageUrl(value);
                    
                    return isLineAttribute && isNotImageUrl && value;
                })
                .map(([key, value]) => ({ key, value }));
        },
        
        isImageUrl(url) {
            if (!url || typeof url !== 'string') return false;
            
            try {
                const urlObj = new URL(url);
                if (!['http:', 'https:'].includes(urlObj.protocol)) return false;
                
                const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
                const pathname = urlObj.pathname.toLowerCase();
                return imageExtensions.some(ext => pathname.endsWith(ext));
            } catch (e) {
                return false;
            }
        },
        
        async applyReset() {
            try {
                this.loading = true;
                this.error = null;
                
                // Validate orderId
                if (!this.orderId) {
                    this.error = 'Order ID ist erforderlich';
                    console.error('❌ Order ID is missing');
                    return;
                }
                
                // Validate that at least one article is selected
                if (this.selectedArticles.length === 0) {
                    this.error = 'Bitte wählen Sie mindestens einen Artikel aus';
                    console.error('❌ No articles selected');
                    return;
                }
                
                console.log('🔧 Resetting articles:', {
                    selected: this.selectedArticles,
                    resetOnly: this.resetOnly,
                    orderId: this.orderId
                });

                // Prepare all selected articles with data expected by controller
                // selectedArticles now contains unique IDs (sku-designId or just sku)
                const items = this.selectedArticles.map(productId => {
                    // Find product by matching unique ID
                    const product = this.orderProducts.find(p => this.getProductUniqueId(p) === productId);
                    if (!product) {
                        console.warn('⚠️ Product not found for ID:', productId);
                        return null;
                    }

                    return {
                        sku: product.sku,
                        translated: product.translated || product.attributes || {},
                        designId: product.designId,
                        qty: product.qty || 1
                    };
                }).filter(item => item !== null);
                
                console.log(`📦 Resetting ${items.length} article(s):`, items.map(i => i.sku).join(', '));
                
                // Call the API to reset/trash the articles (resetOnly defaults to true)
                // Backend expects: orderId (params), items (body), resetOnly (query)
                // Backend returns "Okay" on success
                const response = await PacklistService.applyTrash(this.orderId, items, this.resetOnly !== false);
                
                console.log(`✅ ${items.length} article(s) ${this.resetOnly ? 'reset' : 'marked as damaged'} successfully`);
                console.log('📥 Backend response:', response.data);

                // API call succeeded (backend sent "Okay")
                // Emit success event with all affected articles for local state updates
                // Send both unique IDs and SKUs for compatibility
                this.$emit('reset-confirmed', {
                    selectedArticles: this.selectedArticles, // Unique IDs (sku-designId or sku)
                    selectedSkus: items.map(item => item.sku), // Just SKUs for display/logging
                    totalItems: items.length,
                    resetOnly: this.resetOnly,
                    success: true
                });
                
                // Close dialog
                this.closeModal();
                
            } catch (error) {
                this.error = error.response?.data?.message || error.message || 'Fehler beim Verarbeiten der Artikel';
                console.error('❌ Error processing articles:', error);
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
.damaged-dialog-card {
    border-radius: 16px;
    overflow: hidden;
}

/* Close Button Fixed Position */
.close-button-fixed {
    position: absolute !important;
    top: 12px !important;
    right: 12px !important;
    z-index: 100 !important;
    background-color: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

.close-button-fixed:hover {
    background-color: rgba(255, 255, 255, 1) !important;
    transform: scale(1.05);
}

.enhanced-damaged-header {
    background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
    position: relative;
    z-index: 10;
}

.enhanced-damaged-header.reset-mode {
    background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.enhanced-content {
    background-color: #fafafa;
    min-height: 200px;
}

.article-item {
    max-width: 600px;
    margin: 0 auto;
}

/* Article Image Container */
.article-image-container {
    width: 100%;
    height: 200px;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
}

.article-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.article-sku {
    font-family: 'Roboto Mono', monospace;
    font-size: 1.1rem;
    font-weight: 600;
    color: #e65100;
    background-color: #fff3e0;
    padding: 6px 12px;
    border-radius: 8px;
    display: inline-block;
    border: 1px solid #ffcc02;
}

.scan-status {
    display: flex;
    align-items: center;
}

.attributes-display {
    margin-top: 12px;
}

.attributes-preview {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0,0,0,0.05);
}

.attribute-item {
    display: flex;
    margin-bottom: 6px;
    font-size: 0.9rem;
    line-height: 1.4;
}

.attribute-item:last-child {
    margin-bottom: 0;
}

.attribute-label {
    font-weight: 600;
    color: #424242;
    min-width: 80px;
    margin-right: 12px;
    flex-shrink: 0;
}

.attribute-value {
    color: #212121;
    word-break: break-word;
    flex: 1;
}

.article-content {
    width: 100%;
}

.no-attributes {
    font-style: italic;
    padding: 12px;
    text-align: center;
    color: rgba(0,0,0,0.5);
}



.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 200px;
    padding: 40px;
}

.enhanced-footer {
    background-color: white;
    border-top: 1px solid #e0e0e0;
}


/* Loading state styling */
.damaged-dialog-card.v-card--loading {
    pointer-events: none;
}

.damaged-dialog-card.v-card--loading::before {
    background-color: rgba(255, 255, 255, 0.8);
}

/* Responsive adjustments */
@media (max-width: 900px) {
    .enhanced-damaged-header {
        padding: 16px !important;
    }
    
    .enhanced-content {
        padding: 16px !important;
    }
    
    .enhanced-footer {
        padding: 16px !important;
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
    
    .enhanced-footer .v-btn {
        width: 100%;
        margin: 0 !important;
    }
}

@media (max-width: 600px) {
    .text-h4 {
        font-size: 1.5rem !important;
    }
}

/* Articles Grid Container */
.articles-grid-container {
    width: 100%;
}

.articles-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;
}

/* Article Reset Card */
.article-reset-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    border: 2px solid transparent;
}

.article-reset-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: #ff9800;
}

.article-reset-card.selected {
    border-color: #ff9800;
    box-shadow: 0 4px 16px rgba(255, 152, 0, 0.3);
    background-color: #fff3e0;
}

/* Selection Checkmark */
.selection-checkmark {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
    background-color: #ff9800;
    border-radius: 50%;
    padding: 4px;
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.5);
}

/* Article Preview Image */
.article-preview-image {
    background-color: #f5f5f5;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.image-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.no-image-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background-color: #f0f0f0;
}

/* Article Info */
.article-sku-badge {
    font-family: 'Roboto Mono', monospace;
    font-size: 0.9rem;
    font-weight: 600;
    color: #e65100;
    background-color: #fff3e0;
    padding: 4px 8px;
    border-radius: 6px;
    display: inline-block;
    border: 1px solid #ffcc02;
}

.article-name {
    font-size: 0.85rem;
    color: #666;
    line-height: 1.3;
    min-height: 32px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Responsive Grid */
@media (max-width: 900px) {
    .articles-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
}

@media (max-width: 600px) {
    .articles-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }
}

/* OLD: Article Select Item Styling */
.article-select-item {
    border-bottom: 1px solid #e0e0e0;
}

.article-select-item:hover {
    background-color: #f5f5f5;
}

.article-select-item .v-list-item__avatar {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
}

/* Smooth animations */
.v-enter-active, .v-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter, .v-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

</style>
